import React from 'react';
import type { ActivityViewProps } from '../../core/types/activity.types';
import type { ExploraProjection, SandboxProjection } from '../../core/types/payloads/sandbox.types';
import { formatParts } from './fractionUtils';
import { ExploraLiveStage, ExploraShell } from './TargetReachVisuals';
import { InflationLiveStage, InflationShell } from './InflationLiveStage';

function variantLabel(variant: string) {
  if (variant === 'balanza') return 'Balanza';
  if (variant === 'fracciones') return 'Fracciones';
  if (variant === 'grafico') return 'Economía';
  return 'Mercado';
}

function inflationProjection(
  projection: SandboxProjection | undefined,
): Extract<SandboxProjection, { variant: 'inflation-market' }> | undefined {
  return projection as Extract<SandboxProjection, { variant: 'inflation-market' }> | undefined;
}

function HostContextActions({
  waiting,
  deckMode,
  status,
  onReset,
  onRefreshDeck,
  onEndRound,
}: {
  waiting: boolean;
  deckMode?: boolean;
  status: string;
  onReset: () => void;
  onRefreshDeck: () => void;
  onEndRound: () => void;
}) {
  return (
    <>
      {waiting && (
        <span className="explora-context-hint">Pulsa Comenzar actividad para que interactúen</span>
      )}
      <button type="button" className="explora-context-btn" onClick={onReset}>
        Reiniciar
      </button>
      {deckMode && status === 'PLAYING' && (
        <button type="button" className="explora-context-btn" onClick={onRefreshDeck}>
          Nuevo mazo
        </button>
      )}
      {status === 'PLAYING' && (
        <button type="button" className="explora-context-btn" onClick={onEndRound}>
          Cerrar ronda
        </button>
      )}
    </>
  );
}

function InflationHostActions({
  waiting,
  roundActive,
  onStart,
  onEnd,
}: {
  waiting: boolean;
  roundActive: boolean;
  onStart: () => void;
  onEnd: () => void;
}) {
  return (
    <>
      {waiting && (
        <span className="explora-context-hint">Pulsa Comenzar actividad para abrir el mercado</span>
      )}
      {!roundActive && (
        <button type="button" className="explora-context-btn" onClick={onStart}>
          Iniciar ronda
        </button>
      )}
      {roundActive && (
        <button type="button" className="explora-context-btn" onClick={onEnd}>
          Cerrar ronda
        </button>
      )}
    </>
  );
}

export function SandboxHostView(props: ActivityViewProps<'sandbox'>) {
  const { payload, runtime, dispatch, projection, phase } = props;

  if (payload.variant === 'inflation-market') {
    if (runtime.kind !== 'inflation-market') return null;
    const waiting = phase === 'PREPARING' || phase === 'LOBBY';
    const roundActive = Boolean(runtime.startedAt && runtime.deadlineAt);
    return (
      <InflationShell
        payload={payload}
        runtime={runtime}
        projection={inflationProjection(projection)}
        dispatch={dispatch}
      >
        <InflationHostActions
          waiting={waiting}
          roundActive={roundActive}
          onStart={() => dispatch({ type: 'START_ROUND' })}
          onEnd={() => dispatch({ type: 'END_ROUND' })}
        />
      </InflationShell>
    );
  }

  if (runtime.kind !== 'explora') return null;
  const waiting = phase === 'PREPARING' || phase === 'LOBBY';

  return (
    <ExploraShell
      payload={payload}
      runtime={runtime}
      projection={projection as ExploraProjection | undefined}
      badge={`Explora · ${variantLabel(payload.variant)}`}
    >
      <HostContextActions
        waiting={waiting}
        deckMode={payload.deckMode}
        status={runtime.status}
        onReset={() => dispatch({ type: 'RESET' })}
        onRefreshDeck={() => dispatch({ type: 'REFRESH_DECK' })}
        onEndRound={() => dispatch({ type: 'END_ROUND' })}
      />
    </ExploraShell>
  );
}

export function SandboxStudentView(props: ActivityViewProps<'sandbox'>) {
  const { payload, runtime, dispatch, projection, phase, team } = props;

  if (payload.variant === 'inflation-market') {
    if (runtime.kind !== 'inflation-market') return null;
    if (!team) {
      return <p style={{ color: 'var(--plugin-muted)' }}>Mercado inflacionario requiere equipos.</p>;
    }
    return (
      <InflationLiveStage
        payload={payload}
        runtime={runtime}
        projection={inflationProjection(projection)}
        mode="student"
        fill
        teamId={team.teamId}
        dispatch={dispatch}
      />
    );
  }

  if (runtime.kind !== 'explora') return null;
  const waiting = phase === 'PREPARING' || phase === 'LOBBY';
  const locked = runtime.status !== 'PLAYING';
  const maxValue = payload.maxValue ?? Math.max(payload.targetValue * 2, 20);
  const proj = projection as ExploraProjection | undefined;

  if (waiting) {
    const meta =
      payload.variant === 'fracciones' || payload.variant === 'grafico'
        ? formatParts(payload.targetValue, maxValue)
        : payload.displayEquation ?? `${payload.targetValue} ${payload.unitLabel ?? ''}`;
    return (
      <ExploraLiveStage
        payload={payload}
        runtime={runtime}
        projection={proj}
        mode="student"
        fill
        badge={variantLabel(payload.variant)}
      >
        <span className="explora-context-hint">
          Meta: <strong>{meta}</strong> · Espera a que el profesor pulse Comenzar
        </span>
      </ExploraLiveStage>
    );
  }

  if (locked) {
    return (
      <ExploraLiveStage
        payload={payload}
        runtime={runtime}
        projection={proj}
        mode="student"
        fill
        badge={variantLabel(payload.variant)}
      >
        <span className="explora-context-hint">
          {runtime.status === 'SUCCESS' ? '¡La clase alcanzó el objetivo!' : 'Esta ronda ha terminado.'}
        </span>
      </ExploraLiveStage>
    );
  }

  return (
    <ExploraLiveStage
      payload={payload}
      runtime={runtime}
      projection={proj}
      mode="student"
      fill
      badge={variantLabel(payload.variant)}
      dispatch={dispatch}
    />
  );
}

export function SandboxProjectorView(props: ActivityViewProps<'sandbox'>) {
  const { payload, runtime, projection } = props;

  if (payload.variant === 'inflation-market') {
    if (runtime.kind !== 'inflation-market') return null;
    return (
      <InflationLiveStage
        payload={payload}
        runtime={runtime}
        projection={inflationProjection(projection)}
        mode="projector"
        fill
      />
    );
  }

  if (runtime.kind !== 'explora') return null;

  return (
    <ExploraLiveStage
      payload={payload}
      runtime={runtime}
      projection={projection as ExploraProjection | undefined}
      mode="projector"
      size="lg"
      fill
      badge={`En vivo · ${variantLabel(payload.variant)}`}
    />
  );
}
